import { Options, Vue } from 'vue-class-component';
import store from '@/trust-admin-app/store';
import { Inject, Watch } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { debounce, orderBy } from 'lodash';
import { EventEmitter } from "events";
import { AppInsights } from "@/trust-admin-app/appInsights";
import { InvestigationFilterRequest, InvestigationView, TrustTeamMemberView } from '@/shared/modules/typescript-trustapi-client/models';
import moment from 'moment';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';

@Options({
  components: {
    LoadingIndicator,
    ErrorIndicator,
  },
  mixins: [FormatDateTimeMixin]
})
export default class investigatorInvestigations extends Vue {
  private ai = new AppInsights;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private searchKeyword = '';
  private showActiveStatus = false;
  private showTrainingStatus = false;
  private showCauseDrivenOnly = false;
  private isReplyRequired = false;
  private sortMethod = 'desc';
  private activeSortHeader = 1;

  private tableHeadings = [
    { text: 'Practice (Trading name)', value: 'name', sortable: true, sortMethod: 'acs' },
    { text: 'Branch ID', value: 'practiceBranchId', sortable: true, sortMethod: 'acs' },
    { text: 'Commencement date', value: 'commencementDate', sortable: true, sortMethod: 'desc' },
    { text: 'Investigation type', value: 'investigationTypeName', sortable: true, sortMethod: 'acs' },
    { text: 'Appointment status', value: 'appointmentStatus', sortable: true, sortMethod: 'acs' },
    { text: 'Reply required', value: 'xxx', sortable: true, sortMethod: 'acs' },
    { text: 'Status', value: 'statusName', sortable: true, sortMethod: 'acs' },
  ]

  private investigationsData: InvestigationView[] = [];
  private investigationsDisplay: any[] = [];

  private investigator: InvestigationView = {};

  private loadingContent = false;
  private errorLoadingContent = false;
  private loadingDataSet = false;
  private errorLoadingDataSet = false;
  private showAdvancedFilter = false;

  private srchDebounced: any = null;

  @Watch('searchKeyword')
  private onKeywordChange(): void {
    setTimeout(this.srchDebounced.cancel, 0);
    setTimeout(this.srchDebounced, 0);
  }

  @Watch('showActiveStatus')
  private onShowActiveOnlyChanged(): void {
    this.loadInvestigations();
  }

  @Watch('showTrainingStatus')
  private onShowTrainingStatusChanged(): void {
    this.loadInvestigations();
  }

  @Watch('showCauseDrivenOnly')
  private onShowCauseDrivenOnlyChanged(): void {
    this.loadInvestigations();
  }

  @Watch('isReplyRequired')
  private onIsReplyRequiredChanged(): void {
    this.loadInvestigations();
  }

  @Watch('noInvestigatorAssigned')
  private onNoInvestigatorAssignedChanged(): void {
    this.loadInvestigations();
  }

  private sortTableColumn(index: number, sortable: boolean) {
    if (sortable) {
      if (this.tableHeadings[index].sortable) {
        this.activeSortHeader = index;
        this.sortMethod = this.sortMethod === 'desc' ? 'asc' : 'desc';
        this.tableHeadings[index].sortMethod = this.sortMethod;
        if (this.investigationsDisplay.length > 0) {
          const nameSorter = index == 0 ? (c: any) => c.name.toLowerCase() : [this.tableHeadings[index].value, (c: any) => c.name.toLowerCase()];
          const orderByResults = orderBy(
            this.investigationsDisplay,
            nameSorter,
            [this.sortMethod as any]
          );
          this.investigationsDisplay = orderByResults;
        }
      }
    }
  }

  created(): void {
    this.preloadData();
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('reload-investigation-data', this.loadInvestigations);
    this.srchDebounced = debounce(() => {
      this.loadInvestigations();
    }, 1000);
  }

  private async preloadData(): Promise<void> {
    try {
      const _memberId: any = parseInt(this.$route.query.memberId as string, 10) || '';
      this.loadingContent = true;
      this.errorLoadingDataSet = false;
      const resp: any[] = await Promise.all([
        this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMemberByIdMemberIdGet(_memberId)
      ]);
      this.investigator = resp[0].data.data;
      if (this.investigator.id) {
        await this.loadInvestigations();
        this.loadingContent = false;
      }
    } catch (err) {
      this.errorLoadingDataSet = true;
      this.loadingContent = false;
    } finally {
      this.loadingContent = false;
    }
  }

  private async loadInvestigations() {
    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;
      const request: InvestigationFilterRequest = {
        "trustTeamMemberId": this.investigator.id,
        "keyword": this.searchKeyword,
        "showActiveStatus": this.showActiveStatus,
        "showTrainingStatus": this.showTrainingStatus,
        "investigationCategoryId": this.showCauseDrivenOnly ? 2 : null,
        "isReplyRequired": this.isReplyRequired,
      };

      const resp: any =
        await this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationsByTrustTeamMemberIdPost(
          request
        );

      this.investigationsData = resp.data.data as InvestigationView[];
      this.investigationsData = orderBy(this.investigationsData, ['commencementDate'], 'desc');

      this.investigationsDisplay = [...this.investigationsData];

      // convert to display table
      for (let i = 0; i < this.investigationsData.length; i++) {
        this.investigationsDisplay[i].name = this.investigationsData[i].latestPracticeLogData.branchId ?
          this.investigationsData[i].latestPracticeLogData.name + ' (' + this.investigationsData[i].latestPracticeLogData.tradingName + ')' :
          this.investigationsData[i].latestPracticeLogData.name;

        this.investigationsDisplay[i].practiceBranchId = this.investigationsData[i].latestPracticeLogData.branchId ?
          this.investigationsData[i].latestPracticeLogData.firmId + ' - ' + this.investigationsData[i].latestPracticeLogData.branchId :
          this.investigationsData[i].latestPracticeLogData.firmId.toString();
        this.investigationsDisplay[i].investigationTypeName = this.investigationsData[i].investigationType?.name + ' (' + this.investigationsData[i].investigationCategory?.abbreviation + ')';
        this.investigationsDisplay[i].appointmentStatus = this.investigationsData[i].appointments[0].appointmentStatus.name;

        this.investigationsDisplay[i].statusName = this.investigationsData[i].investigationStatus.name;
      }
      // sort by Commencement date in desc order
      this.sortMethod = 'desc';
      this.activeSortHeader = 2;

      return Promise.resolve(false);
    } catch (err) {
      this.ai.trackException('Error getting investigations: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', err);
      this.errorLoadingContent = true;
      this.loadingContent = false;
      return Promise.reject(true);
    } finally {
      this.loadingContent = false;
    }
  }

  private formatDate(isoStr: string): string {
    return moment(isoStr).format('DD/MM/YYYY');
  }

  private appointmentsTippy(appointments: any[]): string {
    let _htmlString = '<div>';
    for (let i = 0; i < appointments.length; i++) {
      _htmlString += this.$formatDate(appointments[i].startDateTime) + '&nbsp;&nbsp;' + appointments[i].appointmentStatus.name + '<br>';
    }
    _htmlString += '</div>'
    return _htmlString;
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  get selectedTrustTeamMember(): TrustTeamMemberView {
    return store.getters.getSelectedTrustTeamMember;
  }

}

